import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

const BackgroundScene: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Scene, Camera, Renderer
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      60,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    camera.position.z = 6;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    container.appendChild(renderer.domElement);

    // Lights
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.4);
    scene.add(ambientLight);

    const dirLight1 = new THREE.DirectionalLight(0xEFC430, 0.8);
    dirLight1.position.set(5, 5, 5);
    scene.add(dirLight1);

    const pointLight = new THREE.PointLight(0xffffff, 0.5);
    pointLight.position.set(-5, -3, -5);
    scene.add(pointLight);

    // 1. Floating Torus 1
    const torusGeo1 = new THREE.TorusGeometry(1, 0.35, 16, 48);
    const torusMat1 = new THREE.MeshStandardMaterial({
      color: 0xEFC430,
      metalness: 0.85,
      roughness: 0.2,
      transparent: true,
      opacity: 0.35,
    });
    const torus1 = new THREE.Mesh(torusGeo1, torusMat1);
    torus1.position.set(-3.5, 1.5, -3);
    scene.add(torus1);

    // 2. Floating Torus 2
    const torusGeo2 = new THREE.TorusGeometry(0.8, 0.28, 16, 48);
    const torusMat2 = new THREE.MeshStandardMaterial({
      color: 0xD4A017,
      metalness: 0.8,
      roughness: 0.3,
      transparent: true,
      opacity: 0.3,
    });
    const torus2 = new THREE.Mesh(torusGeo2, torusMat2);
    torus2.position.set(4, -1, -5);
    scene.add(torus2);

    // 3. Floating Icosahedron 1
    const icoGeo1 = new THREE.IcosahedronGeometry(0.85, 0);
    const icoMat1 = new THREE.MeshStandardMaterial({
      color: 0xEFC430,
      metalness: 0.7,
      roughness: 0.25,
      wireframe: true,
      transparent: true,
      opacity: 0.35,
    });
    const ico1 = new THREE.Mesh(icoGeo1, icoMat1);
    ico1.position.set(2.5, 2.5, -4);
    scene.add(ico1);

    // 4. Floating Icosahedron 2
    const icoGeo2 = new THREE.IcosahedronGeometry(0.7, 0);
    const icoMat2 = new THREE.MeshStandardMaterial({
      color: 0xF4D675,
      metalness: 0.75,
      roughness: 0.3,
      wireframe: true,
      transparent: true,
      opacity: 0.3,
    });
    const ico2 = new THREE.Mesh(icoGeo2, icoMat2);
    ico2.position.set(-2, -2.5, -6);
    scene.add(ico2);

    // 5. Particle Field
    const particleCount = 140;
    const positions = new Float32Array(particleCount * 3);
    for (let i = 0; i < particleCount; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 20;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 20;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 20;
    }
    const particleGeo = new THREE.BufferGeometry();
    particleGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const particleMat = new THREE.PointsMaterial({
      size: 0.05,
      color: 0xEFC430,
      transparent: true,
      opacity: 0.65,
    });
    const particles = new THREE.Points(particleGeo, particleMat);
    scene.add(particles);

    // Handle Window Resize
    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', handleResize);

    // Animation Loop
    let animationFrameId: number;
    let clock = new THREE.Clock();

    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);
      const elapsedTime = clock.getElapsedTime();

      // Torus 1
      torus1.rotation.x = Math.sin(elapsedTime * 0.3) * 0.4;
      torus1.rotation.y += 0.005;
      torus1.position.y = 1.5 + Math.sin(elapsedTime * 0.5) * 0.2;

      // Torus 2
      torus2.rotation.x += 0.004;
      torus2.rotation.y = Math.cos(elapsedTime * 0.25) * 0.3;
      torus2.position.y = -1 + Math.cos(elapsedTime * 0.4) * 0.2;

      // Icosahedrons
      ico1.rotation.y += 0.006;
      ico1.rotation.z = Math.cos(elapsedTime * 0.2) * 0.3;
      ico1.position.y = 2.5 + Math.sin(elapsedTime * 0.6) * 0.15;

      ico2.rotation.y -= 0.005;
      ico2.rotation.x = Math.sin(elapsedTime * 0.3) * 0.3;
      ico2.position.y = -2.5 + Math.cos(elapsedTime * 0.5) * 0.15;

      // Particles
      particles.rotation.y = elapsedTime * 0.015;
      particles.rotation.x = Math.sin(elapsedTime * 0.01) * 0.08;

      renderer.render(scene, camera);
    };

    animate();

    // Cleanup
    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', handleResize);
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
      torusGeo1.dispose();
      torusMat1.dispose();
      torusGeo2.dispose();
      torusMat2.dispose();
      icoGeo1.dispose();
      icoMat1.dispose();
      icoGeo2.dispose();
      icoMat2.dispose();
      particleGeo.dispose();
      particleMat.dispose();
      renderer.dispose();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none',
      }}
    />
  );
};

export default BackgroundScene;
